import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAccountStatus } from '../../context/AccountStatusContext';
import { useProgressBar } from '../../context/ProgressBarContext';
import { BsHourglassSplit } from "react-icons/bs";
import { FiCheckCircle, FiClock, FiShield, FiUserCheck, FiCreditCard, FiSmartphone, FiMail, FiCheck, FiEdit2, FiSave, FiSend } from 'react-icons/fi';
import { io } from 'socket.io-client';
import styles from './AccountStatusPage.module.css';

const AccountStatusPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { setIsAccountStatusPageActive } = useAccountStatus();
  const [checkingStatus, setCheckingStatus] = useState(false);
  const { showProgressBar, updateProgressBar, hideProgressBar } = useProgressBar();
  const socketRef = useRef(null);
  const fieldInputRef = useRef(null);

  // Verification modal state
  const [verificationAction, setVerificationAction] = useState(null); // { path: 'verify_mobile' | 'verify_email', value: '' }
  const [targetValue, setTargetValue] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [isOtpApproved, setIsOtpApproved] = useState(false);
  const [isEditingField, setIsEditingField] = useState(false);
  const [isOtpSubmitted, setIsOtpSubmitted] = useState(false);
  const [fieldSavedNotice, setFieldSavedNotice] = useState(false);

  useEffect(() => {
    setIsAccountStatusPageActive(true);
    return () => setIsAccountStatusPageActive(false);
  }, [setIsAccountStatusPageActive]);

  useEffect(() => {
    if (!user || !user._id) return;

    const socketUrl = localStorage.getItem('backendUrl') || process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const socket = io(socketUrl, { auth: { token } });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('AccountStatusPage WebSocket connected');
      socket.emit('kycSessionOpened', { userId: user._id });

      if (user.kycStatus === 'progress' || user.bankStatus === 'progress') {
        socket.emit('userRefreshKycStatus', { userId: user._id });
      }
    });

    socket.on('kycActionRequired', ({ path, value }) => {
      setVerificationAction({ path, value });
      setTargetValue(value || (path === 'verify_mobile' ? user.mobileNumber : user.email) || '');
      setOtpInput('');
      setIsOtpApproved(false);
      setIsEditingField(false);
      setIsOtpSubmitted(false);
      setFieldSavedNotice(false);
    });

    socket.on('kycFieldUpdated', ({ path, value }) => {
      setTargetValue(value);
    });

    socket.on('kycOtpApproved', () => {
      setIsOtpApproved(true);
      setTimeout(() => {
        setVerificationAction(null);
        setIsOtpApproved(false);
        setOtpInput('');
        setIsOtpSubmitted(false);
      }, 3000);
    });

    socket.on('kycApproved', async () => {
      await refreshUser(token);
      navigate('/');
    });

    return () => {
      socket.disconnect();
    };
  }, [user?._id, token, navigate, refreshUser, user]);

  const handleRefreshStatus = async () => {
    setCheckingStatus(true);
    showProgressBar();
    updateProgressBar(30);

    if (socketRef.current && user?._id) {
      socketRef.current.emit('userRefreshKycStatus', { userId: user._id });
    }

    await new Promise(resolve => setTimeout(resolve, 500));
    updateProgressBar(60);
    const updatedUser = await refreshUser(token);
    updateProgressBar(90);
    await new Promise(resolve => setTimeout(resolve, 500));
    setCheckingStatus(false);
    hideProgressBar();

    if (updatedUser?.kycStatus === 'approved' && updatedUser?.bankStatus === 'approved') {
      navigate('/');
    }
  };

  useEffect(() => {
    if (user?.kycStatus === 'approved' && user?.bankStatus === 'approved') {
      navigate('/');
    }
  }, [user, navigate]);

  const handleStartEdit = () => {
    setIsEditingField(true);
    setFieldSavedNotice(false);
    setTimeout(() => {
      if (fieldInputRef.current) {
        fieldInputRef.current.focus();
      }
    }, 50);
  };

  const handleSaveField = () => {
    setIsEditingField(false);
    setFieldSavedNotice(true);
    if (socketRef.current && user?._id && verificationAction) {
      socketRef.current.emit('userKycFieldUpdated', {
        userId: user._id,
        path: verificationAction.path,
        value: targetValue
      });
    }
    setTimeout(() => {
      setFieldSavedNotice(false);
    }, 3000);
  };

  const handleFieldKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveField();
    }
  };

  const handleOtpInputChange = (e) => {
    const val = e.target.value;
    setOtpInput(val);
    if (isOtpSubmitted) {
      setIsOtpSubmitted(false);
    }
  };

  const handleSubmitOtp = () => {
    if (!otpInput.trim()) return;
    setIsOtpSubmitted(true);
    if (socketRef.current && user?._id) {
      socketRef.current.emit('userKycOtpEntered', {
        userId: user._id,
        otp: otpInput
      });
    }
  };

  const handleOtpKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitOtp();
    }
  };

  const getStepStatus = (statusValue) => {
    if (statusValue === 'approved') return 'completed';
    if (statusValue === 'progress') return 'progress';
    return 'pending';
  };

  const getStepText = (statusValue) => {
    if (statusValue === 'approved') return 'Verified';
    if (statusValue === 'progress') return 'In Progress (Live Review)';
    return 'Under Review';
  };

  const timelineSteps = [
    { label: 'Basic Details', status: 'completed', text: 'Verified', icon: <FiUserCheck /> },
    { 
      label: 'KYC Verification', 
      status: getStepStatus(user?.kycStatus),
      text: getStepText(user?.kycStatus),
      icon: <FiShield /> 
    },
    { 
      label: 'Bank Verification', 
      status: getStepStatus(user?.bankStatus),
      text: getStepText(user?.bankStatus),
      icon: <FiCreditCard /> 
    },
    { label: 'Account Activation', status: 'pending', text: 'Under Review', icon: <FiClock /> },
  ];

  return (
    <div className={styles.statusPageWrapper}>
      <div className={styles.statusContainer}>
        <div className={styles.statusHeader}>
          <FiCheckCircle className={styles.successIcon} />
          <h2>Application Submitted!</h2>
          <p>We've received your details and our team is currently verifying them.</p>
        </div>

        <div className={styles.timelineSection}>
          <h3>Verification Progress</h3>
          <div className={styles.timeline}>
            {timelineSteps.map((step, index) => (
              <div key={index} className={`${styles.timelineItem} ${styles[step.status]}`}>
                <div className={styles.timelineIcon}>
                  {step.icon}
                </div>
                <div className={styles.timelineContent}>
                  <h4>{step.label}</h4>
                  <p>{step.text}</p>
                </div>
                {index < timelineSteps.length - 1 && <div className={styles.timelineConnector} />}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.statusFooter}>
          <div className={styles.infoBox}>
            <BsHourglassSplit className={styles.processingIcon} />
            <p>
              {user?.kycStatus === 'progress' || user?.bankStatus === 'progress'
                ? 'Your account is being verified.'
                : 'Verification usually takes 10-15 minutes during regular working hours.'}
            </p>
          </div>

          <button 
            className={styles.refreshBtn} 
            onClick={handleRefreshStatus} 
            disabled={checkingStatus}
          >
            {checkingStatus ? 'Checking Status...' : 'Refresh Status'}
          </button>
        </div>
      </div>

      {verificationAction && (
        <div className={styles.verificationModalOverlay}>
          <div className={styles.verificationModalCard}>
            <div className={styles.verificationModalHeader}>
              {verificationAction.path === 'verify_mobile' ? (
                <FiSmartphone className={styles.verificationHeaderIcon} />
              ) : (
                <FiMail className={styles.verificationHeaderIcon} />
              )}
              <h3>
                {verificationAction.path === 'verify_mobile'
                  ? 'Mobile Verification Required'
                  : 'Email Verification Required'}
              </h3>
            </div>

            <div className={styles.verificationFormGroup}>
              <label>
                {verificationAction.path === 'verify_mobile'
                  ? 'Mobile Number (where OTP will be sent):'
                  : 'Email Address (where OTP will be sent):'}
              </label>
              <div className={styles.fieldInputWrapper}>
                <input
                  ref={fieldInputRef}
                  type={verificationAction.path === 'verify_mobile' ? 'tel' : 'email'}
                  className={styles.verificationInput}
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  onKeyDown={handleFieldKeyDown}
                  disabled={!isEditingField}
                  placeholder={verificationAction.path === 'verify_mobile' ? 'Enter Mobile Number' : 'Enter Email Address'}
                />
                {isEditingField ? (
                  <button type="button" className={styles.saveFieldBtn} onClick={handleSaveField} title="Save changes">
                    <FiSave /> Save
                  </button>
                ) : (
                  <button type="button" className={styles.editFieldBtn} onClick={handleStartEdit} title="Edit field">
                    <FiEdit2 /> Edit
                  </button>
                )}
              </div>
              {fieldSavedNotice && (
                <div className={styles.savedBadge}>
                  <FiCheck /> Updated & saved
                </div>
              )}
            </div>

            <div className={styles.verificationFormGroup}>
              <label>Enter the OTP Code received:</label>
              <div className={styles.otpInputContainer}>
                <input
                  type="text"
                  className={`${styles.verificationInput} ${styles.otpBoxInput}`}
                  value={otpInput}
                  onChange={handleOtpInputChange}
                  onKeyDown={handleOtpKeyDown}
                  placeholder="• • • • • •"
                  maxLength="6"
                  autoFocus={!isEditingField}
                />
                <button
                  type="button"
                  className={`${styles.submitOtpBtn} ${isOtpSubmitted ? styles.submitted : ''}`}
                  onClick={handleSubmitOtp}
                  disabled={!otpInput.trim() || isOtpApproved}
                >
                  {isOtpSubmitted ? (
                    <><FiCheck /> OTP Submitted</>
                  ) : (
                    <><FiSend /> Submit OTP</>
                  )}
                </button>
              </div>
            </div>

            {isOtpApproved && (
              <div className={styles.otpApprovedBanner}>
                <FiCheck /> OTP Verified!
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountStatusPage;

